import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { routeBase } from '@/config/router'
import { useUserStore } from '@/stores/user_store'

const showLogoutConfirm = ref(false)
const loggingOut = ref(false)

/**
 * Ends the session and returns to the logged-out Welcome tab, behind the same
 * confirm-before-acting flow as `DeleteConfirmSheet`: `requestLogout` opens the
 * dialog, `confirmLogout` performs the logout, `cancelLogout` dismisses it.
 *
 * Clears the in-memory user profile and navigates to `/welcome` even if the BFF call
 * fails (network error, backend hiccup) — from the UI's perspective logout always
 * succeeds locally; a stray session cookie the BFF failed to clear will simply be
 * rejected by the next authenticated request.
 *
 * `@/api/bff_api` (and its `@/api/http` → `@/router` chain) is imported dynamically
 * inside `confirmLogout()` rather than at module top level. This composable is used
 * by `NavigationButtonPanel`/`MobileBottomNav`, which `App.vue` imports eagerly — a
 * static import here would pull `@/router` into that eager chain ahead of the
 * `@/config/keycloak_config` cycle App.vue already depends on via
 * `useKeycloakHealth`, which reads `router.getRoutes()` at module-eval time (see
 * that file's module comment) and needs `router` to be fully constructed first.
 */
export function useLogout() {
  const router = useRouter()

  function requestLogout() {
    showLogoutConfirm.value = true
  }

  function cancelLogout() {
    showLogoutConfirm.value = false
  }

  async function confirmLogout() {
    if (loggingOut.value) return
    loggingOut.value = true
    try {
      const { logout: logoutRequest } = await import('@/api/bff_api')
      await logoutRequest()
    } catch {
      /* best-effort: still clear local state and navigate away below */
    } finally {
      useUserStore().clearUserInfo()
      loggingOut.value = false
      showLogoutConfirm.value = false
      await router.push(`${routeBase}/welcome`)
    }
  }

  return { showLogoutConfirm, loggingOut, requestLogout, confirmLogout, cancelLogout }
}
